import { defineStore } from 'pinia';
import {
    AgeRange,
    AllFilterSelectors,
    Course,
    CourseStatistics,
    DailyGoal,
    DiaryStatistics,
    FocusStatistics,
    Gender,
    HealthSegment,
    HealthSegmentStatistics,
    MyBody,
    MyBodyStatistics,
    Region,
    Session,
    Statistics,
    Theme,
    Thought,
    ThoughtStatistics,
    TimePeriod,
    UnknownFilterSelectors,
} from '@/types';

/**
 * N.B.: The filters have various complicated rules that are the result of multiple iterations of customer feedback.
 *
 * All the filters have an 'All' option.
 * The 'All' option also shows data where the filter is unknown.
 * For regions specifically, an empty selectedRegions-array is equivalent to selecting all regions.
 * Selecting 'partner regions' adds multiple regions to the selectedRegions-array.
 * Regions is a multiselect on the backend and a single select on the frontend. Both filters write to the same selectedRegions-array.
 *
 * Most filters have an 'Unknown' option. It is voluntary for users whether they want to supply data like gender, region etc. If they don't, the data is filtered as uknown.
 *
 * Events from certain regions should never be included in the data. This is typically because they are test regions and should thus be excluded from the data.
 *
 * There is an optional data cutoff because the collected statistics data has changed over time. If applied, we only show the data from the cutoff date and onwards.
 * Rules for data cutoff:
 * - Courses should never use the data cutoff
 * - Public 'usage data' pages should always use the data cutoff
 * - Admin 'usage data' should use the data cutoff if the applyDataCutoff is true
 */
const filtersStatistics = (state: any, statistics: Statistics[] | null, type: string) => {
    if (!statistics) {
        return [];
    }

    /* Filter excluded regions */
    const exludeResultsFromRegionIds = state.regions ? state.regions.filter((currentRegion: Region) => currentRegion.excludeFromSorting === true).map((currentRegion: Region) => currentRegion.id) : [];
    let result = statistics.filter((currentStatistics) => {
        return currentStatistics.type === type && !exludeResultsFromRegionIds.includes(currentStatistics.region as any);
    });

    /* Filter genders and ageRanges */
    const filters = [
        { selectedProperty: state.selectedGender, statisticsProperty: 'gender', allFilterSelector: AllFilterSelectors.AllGenders, unknownFilterSelector: UnknownFilterSelectors.UnknownGender },
        { selectedProperty: state.selectedAgeRange, statisticsProperty: 'ageRange', allFilterSelector: AllFilterSelectors.AllAgeRanges, unknownFilterSelector: UnknownFilterSelectors.UnknownAgeRange },
    ];
    filters.forEach((currentFilter) => {
        if (currentFilter.selectedProperty && currentFilter.selectedProperty.id !== currentFilter.allFilterSelector) {
            if (currentFilter.selectedProperty.id === currentFilter.unknownFilterSelector) {
                result = result.filter((currentStatistics) => !currentStatistics[currentFilter.statisticsProperty as keyof Statistics]);
            } else {
                result = result.filter((currentStatistics) => currentStatistics[currentFilter.statisticsProperty as keyof Statistics] === currentFilter.selectedProperty?.id);
            }
        }
    });

    /* Filter regions */
    if (state.selectedRegions.length) {
        result = result.filter((currentStatistics) =>
            state.selectedRegions.find((currentSelectedRegion: Region) => {
                return currentSelectedRegion.id === currentStatistics.region || (currentSelectedRegion.id === UnknownFilterSelectors.UnknownRegion && !currentStatistics.region);
            })
        );
    }

    /* Filter data cutoff */
    if (type !== 'course') {
        if (!window.location.href.includes('wp-admin') || state.applyDataCutoff) {
            result = result.filter((currentStatistics) => {
                return new Date(currentStatistics.started) >= state.cutoffDate;
            });
        }
    }

    return result;
};

const useStore = defineStore('main', {
    state: () => {
        return {
            statistics: null as null | Statistics[],
            themes: null as null | Theme[],
            sessions: null as null | Session[],
            courses: null as null | Course[],
            regions: null as null | Region[],
            ageRanges: null as null | AgeRange[],
            dailyGoals: null as null | DailyGoal[],
            healthSegments: null as null | HealthSegment[],
            myBodies: null as null | MyBody[],
            thoughts: null as null | Thought[],
            partnerRegions: null as null | string[] | boolean,
            dataSource: null as null | string,
            applyDataCutoff: null as null | boolean,

            timePeriods: null as null | { text: string; id: TimePeriod | AllFilterSelectors }[],
            genders: null as null | { text: string; id: Gender | AllFilterSelectors | UnknownFilterSelectors }[],

            cutoffDate: null as null | Date,

            selectedPeriod: null as null | { text: string; id: TimePeriod | AllFilterSelectors },
            customDateRange: { from: null, to: null } as { from: string | null; to: string | null },
            selectedCourse: null as null | Course,
            selectedGender: null as null | { text: string; id: Gender | AllFilterSelectors | UnknownFilterSelectors },
            selectedRegions: [] as Region[],
            selectedAgeRange: null as null | AgeRange,
            partnerRegionsSelected: false,
        };
    },
    getters: {
        focusStatistics: (state) => {
            return filtersStatistics(state, state.statistics, 'focus') as FocusStatistics[];
        },
        courseStatistics: (state) => {
            let result = filtersStatistics(state, state.statistics, 'course') as CourseStatistics[];

            if (state.selectedCourse && state.selectedCourse.id !== AllFilterSelectors.AllCourses) {
                result = result.filter((currentStatistics: CourseStatistics) => currentStatistics.ref === state.selectedCourse!.id);
            }
            return result;
        },
        diaryStatistics: (state) => {
            return filtersStatistics(state, state.statistics, 'diary') as DiaryStatistics[];
        },
        healthSegmentStatistics: (state) => {
            return filtersStatistics(state, state.statistics, 'health_segments') as HealthSegmentStatistics[];
        },
        myBodyStatistics: (state) => {
            return filtersStatistics(state, state.statistics, 'my_body') as MyBodyStatistics[];
        },
        thoughtStatistics: (state) => {
            return filtersStatistics(state, state.statistics, 'thoughts') as ThoughtStatistics[];
        },
    },
    actions: {
        init() {
            this.getApplyDataCutoff(true);
            this.getStaticStatistics(true);
            this.getPartnerRegions(true);
            this.getHardcodedStatistics();
        },
        async getHardcodedStatistics() {
            this.timePeriods = [
                { id: TimePeriod.LastSevenDays, text: 'Sidste 7 dage' },
                { id: TimePeriod.LastThirtyDays, text: 'Sidste 30 dage' },
                { id: TimePeriod.LastNinetyDays, text: 'Sidste 90 dage' },
                { id: TimePeriod.LastYear, text: 'Sidste 365 dage' },
                { id: TimePeriod.CustomRange, text: 'Tilpasset periode' },
                { id: AllFilterSelectors.AllPeriods, text: AllFilterSelectors.AllPeriods },
            ];
            this.genders = [
                { id: AllFilterSelectors.AllGenders, text: AllFilterSelectors.AllGenders },
                { id: UnknownFilterSelectors.UnknownGender, text: UnknownFilterSelectors.UnknownGender },
                { id: Gender.Female, text: Gender.Female },
                { id: Gender.Male, text: Gender.Male },
                { id: Gender.Other, text: Gender.Other },
            ];
            this.selectedPeriod = this.timePeriods[1];
            this.selectedGender = this.genders[0];
        },
        async getStaticStatistics(bustCache: boolean = false) {
            if (this.statistics === null || bustCache) {
                const fetchResult = await fetch('/wp-json/mitliv/v1/static-statistics', {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${process.env.VUE_APP_STATISTICS_API_KEY}`,
                    },
                });
                const fetchResultData: {
                    success: boolean;
                    error: null;
                    data: {
                        themes: Theme[];
                        sessions: Session[];
                        courses: Course[];
                        regions: Region[];
                        ageRanges: AgeRange[];
                        dailyGoals: DailyGoal[];
                        healthSegments: HealthSegment[];
                        myBody: MyBody[];
                        thoughts: Thought[];
                        cutoff: string;
                    };
                } = await fetchResult.json();

                this.courses = [{ title: AllFilterSelectors.AllCourses, id: AllFilterSelectors.AllCourses, themes: [] }, ...fetchResultData.data.courses.sort((a, b) => a.title.localeCompare(b.title))];
                this.regions = [
                    { title: AllFilterSelectors.AllRegions, id: AllFilterSelectors.AllRegions },
                    { title: UnknownFilterSelectors.UnknownRegion, id: UnknownFilterSelectors.UnknownRegion },
                    ...fetchResultData.data.regions.sort((a, b) => a.title.localeCompare(b.title)),
                ];
                this.ageRanges = [
                    { title: AllFilterSelectors.AllAgeRanges, id: AllFilterSelectors.AllAgeRanges, sortOrder: 0 },
                    { title: UnknownFilterSelectors.UnknownAgeRange, id: UnknownFilterSelectors.UnknownAgeRange, sortOrder: 1 },
                    ...fetchResultData.data.ageRanges.sort((a, b) => a.title.localeCompare(b.title)),
                ];

                this.themes = fetchResultData.data.themes.sort((a, b) => a.title.localeCompare(b.title));
                this.dailyGoals = fetchResultData.data.dailyGoals.sort((a, b) => (a.title || a.type).localeCompare(b.title || b.type));
                this.healthSegments = fetchResultData.data.healthSegments.sort((a, b) => a.title.localeCompare(b.title));
                this.myBodies = fetchResultData.data.myBody.sort((a, b) => a.title.localeCompare(b.title));
                this.thoughts = fetchResultData.data.thoughts.sort((a, b) => a.title.localeCompare(b.title));
                this.sessions = fetchResultData.data.sessions;
                this.cutoffDate = new Date(fetchResultData.data.cutoff);

                this.selectedAgeRange = this.ageRanges[0];
                this.selectedCourse = this.courses[0];
            }
        },
        async getStatistics(bustCache: boolean = false) {
            if (this.statistics === null || bustCache) {
                if (this.selectedPeriod?.id === TimePeriod.CustomRange) {
                    const fromDate = this.customDateRange.from ? new Date(this.customDateRange.from) : null;
                    const toDate = this.customDateRange.to ? new Date(this.customDateRange.to) : null;

                    if (
                        !fromDate ||
                        !toDate ||
                        Number.isNaN(fromDate.getTime()) ||
                        Number.isNaN(toDate.getTime()) ||
                        fromDate.getFullYear() < 1000 ||
                        toDate.getFullYear() < 1000
                    ) {
                        return false;
                    }
                }

                const params = new URLSearchParams({ from: String(this.selectedPeriod?.id) });
                if (this.selectedPeriod?.id === TimePeriod.CustomRange && this.customDateRange.from && this.customDateRange.to) {
                    params.set('customFrom', this.customDateRange.from);
                    params.set('customTo', this.customDateRange.to);
                }

                const fetchResult = await fetch(`/wp-json/mitliv/v1/statistics?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${process.env.VUE_APP_STATISTICS_API_KEY}`,
                    },
                });
                const fetchResultData: { success: boolean; error: null; data: { statistics: Statistics[] } } = await fetchResult.json();
                this.statistics = fetchResultData.data.statistics;
            }
            return true;
        },
        async getPartnerRegions(bustCache: boolean = false) {
            if (this.partnerRegions === null || bustCache) {
                const fetchResult = await fetch(`/wp-json/mitliv/v1/partner-regions`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${process.env.VUE_APP_STATISTICS_API_KEY}`,
                    },
                });
                const fetchResultData: { success: boolean; error: null; data: { regions: string[] } } = await fetchResult.json();
                this.partnerRegions = fetchResultData.data.regions;
            }
            return true;
        },
        async setPartnerRegions(partnerRegions: string[]) {
            await fetch(`/wp-json/mitliv/v1/partner-regions`, {
                method: 'POST',
                body: JSON.stringify({ regions: partnerRegions }),
                headers: {
                    Authorization: `Bearer ${process.env.VUE_APP_STATISTICS_API_KEY}`,
                },
            });

            return true;
        },
        async getDataSource(bustCache: boolean = false) {
            if (this.dataSource === null || bustCache) {
                const fetchResult = await fetch(`/wp-json/mitliv/v1/data-source`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${process.env.VUE_APP_STATISTICS_API_KEY}`,
                    },
                });
                const fetchResultData: { success: boolean; error: null; data: { dataSource: string } } = await fetchResult.json();
                this.dataSource = fetchResultData.data.dataSource;
            }
            return true;
        },
        async setDataSource(dataSource: string) {
            await fetch(`/wp-json/mitliv/v1/data-source`, {
                method: 'POST',
                body: JSON.stringify({ dataSource }),
                headers: {
                    Authorization: `Bearer ${process.env.VUE_APP_STATISTICS_API_KEY}`,
                },
            });

            return true;
        },
        async getApplyDataCutoff(bustCache: boolean = false) {
            if (this.applyDataCutoff === null || bustCache) {
                const fetchResult = await fetch(`/wp-json/mitliv/v1/apply-data-cutoff`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${process.env.VUE_APP_STATISTICS_API_KEY}`,
                    },
                });
                const fetchResultData: { success: boolean; error: null; data: { applyDataCutoff: boolean } } = await fetchResult.json();
                this.applyDataCutoff = fetchResultData.data.applyDataCutoff;
            }
            return true;
        },
        async setApplyDataCutoff(applyDataCutoff: boolean) {
            await fetch(`/wp-json/mitliv/v1/apply-data-cutoff`, {
                method: 'POST',
                body: JSON.stringify({ applyDataCutoff }),
                headers: {
                    Authorization: `Bearer ${process.env.VUE_APP_STATISTICS_API_KEY}`,
                },
            });

            return true;
        },
    },
});

export { useStore };
